import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { AppSettingsService } from '@proxy/settings/app-settings.service';
import { ColumnListPreferences, UpdateColumnListPreferencesInput } from '@proxy/settings/models';
import { defaultAssetPreferences } from '../../shared/constants';

/**
 * T016-T023: Column Preference Service
 * Manages asset column visibility, order, and width preferences with local cache
 * Uses BehaviorSubject for reactive state management
 * Updated to use Dictionary<string, ColumnConfig> structure matching backend
 */
@Injectable({
  providedIn: 'root'
})
export class ColumnPreferenceService {
  /**
   * T016: BehaviorSubject cache for column preferences
   * Initialized to null - will be populated on first load
   */
  private preferencesSubject = new BehaviorSubject<ColumnListPreferences | null>(null);

  constructor(private settingsService: AppSettingsService) {}

  /**
   * T017: Load preferences from backend API
   * Populates the BehaviorSubject cache
   *
   * @returns Promise that resolves when preferences are loaded
   * @throws Error if API call fails (defaults are used as fallback)
   *
   * @example
   * ```typescript
   * await columnPreferenceService.loadPreferences();
   * ```
   */
  async loadPreferences(): Promise<void> {
    try {
      const response = await firstValueFrom(this.settingsService.getAssetListPreferences());
      this.preferencesSubject.next(response);
    } catch (error) {
      console.error('Failed to load column preferences:', error);
      // On error, use defaults
      this.preferencesSubject.next({ ...defaultAssetPreferences });
      throw error;
    }
  }

  /**
   * T018: Get current preferences as observable
   * Allows components to reactively subscribe to preference changes
   *
   * @returns Observable that emits current preferences whenever they change
   * @returns null if preferences haven't been loaded yet
   *
   * @example
   * ```typescript
   * this.columnPreferenceService.getPreferences().subscribe(prefs => {
   *   if (prefs) {
   *     const orderedColumns = this.getOrderedColumnIds(prefs);
   *     const widths = this.getColumnWidths(prefs);
   *   }
   * });
   * ```
   */
  getPreferences(): Observable<ColumnListPreferences | null> {
    return this.preferencesSubject.asObservable();
  }

  /**
   * Get current preferences synchronously from the BehaviorSubject cache
   */
  getCurrentPreferences(): ColumnListPreferences | null {
    return this.preferencesSubject.value;
  }

  /**
   * Helper: Get ordered column IDs (visible columns sorted by order)
   *
   * @param prefs Asset preferences
   * @returns Array of column IDs sorted by order, filtered to visible only
   */
  getOrderedColumnIds(prefs: ColumnListPreferences): string[] {
    return Object.entries(prefs.columns)
      .filter(([_, config]) => config.visible)
      .sort((a, b) => a[1].order - b[1].order)
      .map(([columnId, _]) => columnId);
  }

  /**
   * Helper: Get column widths map
   *
   * @param prefs Asset preferences
   * @returns Map of columnId to width (only includes columns with width > 0)
   */
  getColumnWidths(prefs: ColumnListPreferences): { [columnId: string]: number } {
    const widths: { [columnId: string]: number } = {};
    Object.entries(prefs.columns).forEach(([columnId, config]) => {
      if (config.width > 0) {
        widths[columnId] = config.width;
      }
    });
    return widths;
  }

  /**
   * Helper: Get mandatory column IDs
   *
   * @param prefs Asset preferences
   * @returns Array of column IDs that are mandatory
   */
  getMandatoryColumnIds(prefs: ColumnListPreferences): string[] {
    return Object.entries(prefs.columns)
      .filter(([_, config]) => config.mandatory)
      .map(([columnId, _]) => columnId);
  }

  /**
   * T019: Update column visibility in local cache
   * Updates which columns are visible without persisting to backend
   * Call savePreferences() to persist changes
   *
   * @param columnId Column identifier to toggle
   * @param visible Whether the column should be visible
   *
   * @example
   * ```typescript
   * service.updateColumnVisibility('barcode', true);
   * await service.savePreferences(); // Persist to backend
   * ```
   */
  updateColumnVisibility(columnId: string, visible: boolean): void {
    const current = this.preferencesSubject.value || { ...defaultAssetPreferences };

    if (current.columns[columnId]) {
      current.columns[columnId].visible = visible;
      this.preferencesSubject.next({ ...current });
    }
  }

  /**
   * T020: Update column order in local cache
   * Changes the display order of columns without persisting to backend
   * Call savePreferences() to persist changes
   *
   * @param newColumnOrder Array of column IDs in the desired display order
   *
   * @example
   * ```typescript
   * // Reorder columns with barcode first
   * service.updateColumnOrder(['barcode', 'primaryID', 'secondaryID']);
   * await service.savePreferences(); // Persist to backend
   * ```
   */
  updateColumnOrder(newColumnOrder: string[]): void {
    const current = this.preferencesSubject.value || { ...defaultAssetPreferences };

    // Update order property for each column
    newColumnOrder.forEach((columnId, index) => {
      if (current.columns[columnId]) {
        current.columns[columnId].order = index + 1; // 1-based order
      }
    });

    this.preferencesSubject.next({ ...current });
  }

  /**
   * T021: Update column width in local cache
   * Sets custom width for a specific column without persisting to backend
   * Call savePreferences() to persist changes
   *
   * @param columnId Column identifier (e.g., 'PrimaryID', 'Barcode')
   * @param width Width in pixels
   *
   * @example
   * ```typescript
   * // Set PrimaryID column to 300px wide
   * service.updateColumnWidth('PrimaryID', 300);
   * await service.savePreferences(); // Persist to backend
   * ```
   */
  updateColumnWidth(columnId: string, width: number): void {
    const current = this.preferencesSubject.value || { ...defaultAssetPreferences };

    if (current.columns[columnId]) {
      current.columns[columnId].width = width;
      this.preferencesSubject.next({ ...current });
    }
  }

  /**
   * T022: Save current preferences to backend
   * Persists the current BehaviorSubject state to the server
   *
   * @returns Promise that resolves when preferences are saved successfully
   * @throws Error if API call fails or no preferences to save
   *
   * @example
   * ```typescript
   * try {
   *   await service.savePreferences();
   *   console.log('Preferences saved successfully');
   * } catch (error) {
   *   console.error('Failed to save:', error);
   * }
   * ```
   */
  async savePreferences(): Promise<void> {
    const currentPrefs = this.preferencesSubject.value;

    if (!currentPrefs) {
      throw new Error('No preferences to save');
    }

    try {
      // Transform currentPrefs to the API format:
      // Only visible columns are included; order of entries encodes display order; value is width.
      const orderedVisibleEntries = Object.entries(currentPrefs.columns)
        .filter(([_, config]) => config.visible)
        .sort((a, b) => a[1].order - b[1].order);

      const prefsInput: UpdateColumnListPreferencesInput = {
        columns: orderedVisibleEntries.reduce((acc, [columnId, config]) => {
          acc[columnId] = config.width;
          return acc;
        }, {} as { [columnId: string]: number })
      };
      await firstValueFrom(this.settingsService.setAssetListPreferences(prefsInput));
    } catch (error) {
      console.error('Failed to save column preferences:', error);
      throw error;
    }
  }

  /**
   * T023: Reset preferences to system defaults
   * Resets local cache to default column configuration and persists to backend
   * Default configuration: PrimaryID, SecondaryID, CategoryNickname,
   * HomeLocationName, SecondaryLocationName with no custom widths
   *
   * @returns Promise that resolves when reset is complete and saved
   * @throws Error if save operation fails
   *
   * @example
   * ```typescript
   * // Reset to defaults after user confirmation
   * if (confirm('Reset to default columns?')) {
   *   await service.resetToDefaults();
   * }
   * ```
   */
  async resetToDefaults(): Promise<void> {
    this.preferencesSubject.next({ ...defaultAssetPreferences });

    try {
      await this.savePreferences();
    } catch (error) {
      console.error('Failed to reset preferences:', error);
      throw error;
    }
  }
}
